/**
 * Location Intelligence Service - API calls for location heatmap and alerts
 */

import { createApiClient } from '@shared/auth';
import apiConfig from '../config/apiConfig';
import { extractErrorMessage } from '../utils/responseHelpers';

const client = createApiClient(apiConfig.getLocationServiceUrl());

// ==================== Heatmap & Summary ====================

export const getHeatmapData = async (params = {}) => {
  try {
    const response = await client.get('/locations/heatmap', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch heatmap data.') };
  }
};

export const getLocationSummary = async (params = {}) => {
  try {
    const response = await client.get('/locations/summary', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch location summary.') };
  }
};

// ==================== Areas ====================

export const getLocationAreas = async (params = {}) => {
  try {
    const response = await client.get('/locations/areas', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch location areas.') };
  }
};

export const getAreaDetail = async (areaKey, params = {}) => {
  try {
    const response = await client.get(`/locations/areas/${encodeURIComponent(areaKey)}`, { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch area detail.') };
  }
};

export const getAreaInteractionIds = async (areaKey, params = {}) => {
  try {
    const response = await client.get(`/locations/areas/${encodeURIComponent(areaKey)}/interaction-ids`, { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch city interaction ids.') };
  }
};

// Bulk lookup: returns { locations: { <interaction_id>: {city, state, country} }, total }
export const getLocationsByInteractionIds = async (ids = []) => {
  try {
    if (!ids.length) return { success: true, data: { locations: {}, total: 0 } };
    const response = await client.get('/locations/by-interactions', {
      params: { interaction_ids: ids.join(',') },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch locations.') };
  }
};

// ==================== Insights ====================

export const getAreaInsights = async (areaKey) => {
  try {
    const response = await client.get(`/locations/areas/${encodeURIComponent(areaKey)}/insights`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch area insights.') };
  }
};

export const generateAreaInsights = async (areaKey) => {
  try {
    const response = await client.post(`/locations/areas/${encodeURIComponent(areaKey)}/insights/generate`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to generate insights.') };
  }
};

// ==================== Alert Rules ====================

export const getAlertRules = async (params = {}) => {
  try {
    const response = await client.get('/locations/alert-rules', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch alert rules.') };
  }
};

export const createAlertRule = async (data) => {
  try {
    const response = await client.post('/locations/alert-rules', data);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to create alert rule.') };
  }
};

export const updateAlertRule = async (ruleId, data) => {
  try {
    const response = await client.put(`/locations/alert-rules/${ruleId}`, data);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to update alert rule.') };
  }
};

export const deleteAlertRule = async (ruleId) => {
  try {
    await client.delete(`/locations/alert-rules/${ruleId}`);
    return { success: true };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to delete alert rule.') };
  }
};

// ==================== Alerts ====================

export const getAlerts = async (params = {}) => {
  try {
    const response = await client.get('/locations/alerts', { params });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to fetch alerts.') };
  }
};

export const acknowledgeAlert = async (alertId) => {
  try {
    const response = await client.post(`/locations/alerts/${alertId}/acknowledge`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error, 'Failed to acknowledge alert.') };
  }
};
